/**
 * Bold and italic, without `document.execCommand`.
 *
 * Selection is carried across the edit as a bookmark rather than as live node
 * references: toggling a mark changes structure but never the text, the line
 * breaks or the block count, so the linear offsets a bookmark stores are
 * unaffected by definition.
 */

import * as bookmark from './bookmark';
import {
  blockOf,
  blocksInRange,
  isolate,
  markAncestor,
  mergeInlines,
  splitRangeBoundaries,
  unwrap,
} from './dom-utils';
import { outdentItem } from './lists';

export type ToggleableMark = 'B' | 'I';

/** Everything "clear formatting" strips from the selected text. */
const MARK_TAGS_TO_CLEAR = ['A', 'B', 'I'] as const;

/**
 * True when every text leaf in the range already carries the mark. Drives the
 * toolbar's `aria-pressed` state, so it must not mutate the tree.
 */
export function hasMarkThroughout(root: HTMLElement, range: Range, tag: ToggleableMark): boolean {
  if (range.collapsed) {
    return markAncestor(range.startContainer, root, tag) !== null;
  }
  const leaves = coveredLeaves(root, range);
  return leaves.length > 0 && leaves.every((leaf) => markAncestor(leaf, root, tag) !== null);
}

/** Text leaves the range touches, ignoring whitespace between blocks. */
function coveredLeaves(root: HTMLElement, range: Range): Text[] {
  const leaves: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const text = node as Text;
    if (text.data.trim().length > 0 && range.intersectsNode(text)) {
      leaves.push(text);
    }
    node = walker.nextNode();
  }
  return leaves;
}

/**
 * Toggles a mark over the selection. Follows the Word/Docs semantic: if every
 * covered leaf already has the mark, remove it; otherwise apply it everywhere.
 */
export function toggleMark(root: HTMLElement, range: Range, tag: ToggleableMark): void {
  const mark = bookmark.save(root);
  const leaves = splitRangeBoundaries(root, range);
  if (leaves.length === 0) {
    return;
  }

  const shouldAdd = !leaves.every((leaf) => markAncestor(leaf, root, tag) !== null);
  const touchedBlocks = new Set<Element>();

  for (const leaf of leaves) {
    const block = blockOf(leaf, root);
    if (block) {
      touchedBlocks.add(block);
    }

    const existing = markAncestor(leaf, root, tag);
    if (shouldAdd) {
      if (!existing) {
        const wrapper = document.createElement(tag.toLowerCase());
        leaf.before(wrapper);
        wrapper.appendChild(leaf);
      }
    } else if (existing) {
      unwrap(isolate(leaf, existing));
    }
  }

  for (const block of touchedBlocks) {
    mergeInlines(block);
  }

  bookmark.restore(root, mark);
}

/**
 * Removes every applied formatting from the selection: bold, italic, links and
 * list structure, matching what "clear formatting" does in Word and Docs.
 *
 * With nothing selected it falls back to removing the link under the caret,
 * which is the one case where an author reasonably expects a bare caret to do
 * something -- there is no other way to unlink without selecting first.
 */
export function clearFormatting(root: HTMLElement, range: Range): void {
  const mark = bookmark.save(root);

  if (range.collapsed) {
    const anchor = markAncestor(range.startContainer, root, 'A');
    if (anchor) {
      const block = blockOf(anchor, root);
      unwrap(anchor);
      if (block) {
        mergeInlines(block);
      }
    }
    bookmark.restore(root, mark);
    return;
  }

  // Capture the blocks before the tree is rewritten underneath us.
  const listItems = blocksInRange(root, range).filter((block) => block.nodeName === 'LI');
  const leaves = splitRangeBoundaries(root, range);
  const touchedBlocks = new Set<Element>();

  for (const leaf of leaves) {
    const block = blockOf(leaf, root);
    if (block) {
      touchedBlocks.add(block);
    }
    for (const tag of MARK_TAGS_TO_CLEAR) {
      let ancestor = markAncestor(leaf, root, tag);
      while (ancestor) {
        unwrap(isolate(leaf, ancestor));
        ancestor = markAncestor(leaf, root, tag);
      }
    }
  }

  for (const block of touchedBlocks) {
    mergeInlines(block);
  }

  // Lists last: outdenting moves nodes between parents, and the inline pass
  // above needs the marks' original ancestry to still be intact.
  for (const item of listItems) {
    if (item.isConnected) {
      outdentItem(item);
    }
  }

  bookmark.restore(root, mark);
}
